import { HttpClient } from '@angular/common/http';
import { LogService } from '@ups/xplat/core';
import { FormRole } from '@ups/xplat/api/dto';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  DynamicModelType,
  ICannedResponse,
  IDynamicContainerMetadata,
  IDynamicControl,
  IDynamicResponseBody,
  IFormReview,
} from '../models';
import { dynamicSaveErrorResult } from '../utils/dynamic-save-result';
import {
  buildContainerListRequest,
  DynamicContainerListResponse,
  IODataResponse,
  toArray,
} from './container-list-source';
import { IDynamicContainerListParams } from './dynamic-container-list';

// various debug logs which can be isolated
const logTag = 'DynamicRenderService';

/** Collaborators read lazily so specs can swap them on the owning service. */
export interface IDynamicRenderApiDeps {
  http: () => HttpClient;
  log: () => LogService;
}

/**
 * The Dynamic Content API calls of DynamicRenderService (WI 31925: split out of that service, unchanged).
 * As before, a loader's promise settles only on a response: a failed request leaves it pending.
 */
export class DynamicRenderApi {
  constructor(private readonly deps: IDynamicRenderApiDeps) {}

  private get http() {
    return this.deps.http();
  }
  private get log() {
    return this.deps.log();
  }

  /**
   * Every version of the app's containers; resolves only with a list. `appTagName` is read and `onLoaded` runs
   * inside the request, as when this was DynamicRenderService.loadAllVersionContainerList.
   */
  loadAllVersions(
    appTagName: () => string,
    onLoaded: (list: Array<IDynamicContainerMetadata>) => void
  ): Promise<Array<IDynamicContainerMetadata>> {
    return new Promise((resolve) => {
      const params: string[] = [];
      // HttpParams encodes the tag, so pass it raw (#31540)
      const tagFilter = ` tag/tagName eq '${decodeURIComponent(appTagName())}'`;
      params.push(`tags/any(tag:${tagFilter})`);

      this.http
        .get<IODataResponse<IDynamicContainerMetadata>>(
          `/api/DynamicContainer/all/odata`,
          {
            params: {
              $filter: params.join(' and '),
              $format: 'json',
            },
          }
        )
        .pipe(
          catchError((err) => {
            this.log.debug(logTag, 'ERROR:', err);
            return of<IODataResponse<IDynamicContainerMetadata> | null>(null);
          })
        )
        .subscribe((list) => {
          if (list?.value) {
            onLoaded(list.value);
            resolve(list.value);
          }
        });
    });
  }

  /** One container-list feed (DynamicRenderService.loadContainerList); an error is logged and read as null. */
  loadContainerList(
    usingAll: boolean,
    httpUrl: string,
    httpParams: IDynamicContainerListParams,
    appTag: string,
    publishedOnly: boolean
  ): Observable<DynamicContainerListResponse> {
    return usingAll
      ? buildContainerListRequest(
          (url) =>
            this.http.get<IDynamicContainerMetadata[]>(url, {
              params: httpParams,
            }),
          (err) => this.log.debug(logTag, 'ERROR:', err),
          httpUrl,
          `/api/DynamicContainer/all?tagNames=${appTag}`,
          publishedOnly
        )
      : this.http
          .get<IODataResponse<IDynamicContainerMetadata>>(httpUrl, {
            params: httpParams,
          })
          .pipe(
            catchError((err) => {
              this.log.debug(logTag, 'ERROR:', err);
              return of<IODataResponse<IDynamicContainerMetadata> | null>(null);
            })
          );
  }

  loadFormRoles(): Promise<Array<FormRole>> {
    return this.getList<FormRole>(`/api/form/roles`);
  }

  loadContainersIncludingControls(
    tagNames: string[] | null
  ): Promise<IDynamicContainerMetadata[]> {
    const queryParameters: string[] = [];

    if (tagNames) {
      tagNames.forEach((i) => {
        queryParameters.push(`tagNames=${i}`);
      });
    }

    const url =
      '/api/DynamicContainer/all' +
      (queryParameters.length ? '?' + queryParameters.join('&') : '');

    return this.getList<IDynamicContainerMetadata>(url);
  }

  /** `onLoaded` runs before the promise resolves, as it did inside DynamicRenderService.loadContainerDetails. */
  loadContainerDetails(
    metadata: IDynamicContainerMetadata,
    onLoaded: (container: IDynamicContainerMetadata) => void
  ): Promise<IDynamicContainerMetadata> {
    const versionNumber = metadata.versionNumber ?? 1;
    const id =
      versionNumber > 1 && metadata.originalVersionContainerId
        ? metadata.originalVersionContainerId
        : metadata.dynamicContainerId;
    return this.getOne<IDynamicContainerMetadata>(
      `/api/DynamicContainer/${id}/container/${versionNumber}`,
      onLoaded
    );
  }

  loadReusableControls(): Promise<Array<IDynamicControl>> {
    return this.getList<IDynamicControl>(`/api/DynamicControl/reusable`);
  }

  loadReportCategories(type?: DynamicModelType): Promise<Array<string>> {
    return this.getList<string>(
      `/api/DynamicControl/${type ? type + '/' : ''}reportCategoryNames`
    );
  }

  loadContainerResponse(
    dynamicResponseId: string
  ): Promise<IDynamicResponseBody> {
    return this.getOne<IDynamicResponseBody>(
      `/api/DynamicResponse/${dynamicResponseId}`
    );
  }

  /** PUT when `existingContainerId` is set, POST otherwise; an error is emitted as the value. */
  saveContainer(body: IDynamicContainerMetadata, existingContainerId?: string) {
    const request$ = existingContainerId
      ? this.http.put(`/api/DynamicContainer/${existingContainerId}`, body)
      : this.http.post(`/api/DynamicContainer`, body);
    return request$.pipe(
      catchError((err) => {
        this.log.debug(logTag, 'ERROR:', err);
        return of(err);
      })
    );
  }

  publishContainer(containerId?: string, version?: number) {
    return this.http
      .post(`/api/DynamicContainer/publish/${containerId}/${version}`, null)
      .pipe(
        catchError((err) => {
          this.log.debug(logTag, 'ERROR:', err);
          // Surface the error to the caller instead of swallowing it to a null
          // that reads as success. A publish can fail intermittently (SQL
          // "New transaction is not allowed..." concurrency error); when it does
          // the admin form-builder must warn the user, because a save-then-failed-
          // publish leaves a higher unpublished version that shadows the live one.
          return of(err);
        })
      );
  }

  saveResponse(
    body: IDynamicResponseBody
  ): Observable<IDynamicResponseBody | null> {
    return this.http.post('/api/DynamicResponse', body).pipe(
      catchError((err) => {
        this.log.debug(logTag, 'ERROR:', err);
        return of(dynamicSaveErrorResult(err));
      })
    );
  }

  updateResponse(body: IDynamicResponseBody) {
    return this.http
      .put('/api/DynamicResponse/' + body.dynamicContainerResponseId, body)
      .pipe(
        catchError((err) => {
          this.log.debug(logTag, 'ERROR:', err);
          return of(null);
        })
      );
  }

  saveFormReview(dto: IFormReview) {
    return this.http.post(`/api/Form/coach`, dto);
  }

  getFormReview(
    dynamicResponseId: string,
    type: string
  ): Observable<IFormReview> {
    const url = `/api/form/review/${type}/${dynamicResponseId}`;

    return this.http.get(url) as Observable<IFormReview>;
  }

  getCannedResponses(type?: string): Observable<ICannedResponse[]> {
    let url = '/api/form/cannedResponses';
    if (type) {
      url += `?type=${type}`;
    }
    return this.http.get(url) as Observable<ICannedResponse[]>;
  }

  aiCoach(
    dynamicContainerResponseId: string,
    createReviewRecord: boolean
  ): Observable<IFormReview> {
    let url = `/api/dynamicResponse/${dynamicContainerResponseId}/aiCoach`;

    if (createReviewRecord) {
      url += `?createReviewRecord=true`;
    }

    return this.http.post(url, {
      dynamicContainerResponseId,
    }) as Observable<IFormReview>;
  }

  /** Resolves with the response as an array; a failed request (null) never resolves. */
  private getList<T>(url: string): Promise<Array<T>> {
    return new Promise((resolve) => {
      this.http
        .get<T[]>(url)
        .pipe(catchError(() => of<T[] | null>(null)))
        .subscribe((items) => {
          if (items != null) {
            resolve(toArray(items));
          }
        });
    });
  }

  /** Resolves with a truthy response; a failed request (null) never resolves. */
  private getOne<T>(url: string, onLoaded?: (item: T) => void): Promise<T> {
    return new Promise((resolve) => {
      this.http
        .get<T>(url)
        .pipe(catchError(() => of<T | null>(null)))
        .subscribe((item) => {
          if (item) {
            onLoaded?.(item);
            resolve(item);
          }
        });
    });
  }
}
